"use client";

import { ArrowRight, MinusIcon, PlusIcon } from "lucide-react";
import Link from "next/link";
import * as React from "react";

import parse from "@/components/html-parser";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "@/components/ui/accordion";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { Page } from "@/types";

interface OtherFaqsProps {
	faqs: any;
	other_faqs: any;
	page: Page;
}

export function OtherFaqs({ faqs, other_faqs, page }: OtherFaqsProps) {
	if (!faqs || !faqs?.length) return null;

	return (
		<Accordion
			type="single"
			id="frequently-asked-questions"
			defaultValue="frequently-asked-questions"
			collapsible
		>
			<AccordionItem value="frequently-asked-questions">
				<AccordionTrigger
					as="h2"
					copyLinkUrl={page?.isPaidAd ? "#0" : `/${page?.url}/#frequently-asked-questions`}
					className="group flex items-center border-b border-dashed border-current pb-2 md:text-30 3xl:text-40"
				>
					<span>Frequently asked questions</span>
				</AccordionTrigger>

				<AccordionContent className="content">
					{faqs?.map((faq: any) => (
						<div
							key={faq?.slug}
							id={faq?.slug}
							className="my-2 rounded-md border border-gray-200 px-6 py-5 md:my-5"
						>
							<Faqs faq={faq} isSubFaq={false} page={page} />
						</div>
					))}

					{other_faqs?.length &&
						[{ title: " Other FAQs", slug: "other_faqs", children: other_faqs }]?.map(
							(faq: any) => (
								<div
									key={faq?.slug}
									id={faq?.slug}
									className="my-2 rounded-md border border-gray-200 px-6 py-5 md:my-5"
								>
									<Faqs faq={faq} isSubFaq={false} page={page} />
								</div>
							)
						)}
				</AccordionContent>
			</AccordionItem>
		</Accordion>
	);
}

function Faqs({ faq, isSubFaq = false, page }: any) {
	const [open, setOpen] = React.useState(true);

	if (!faq) return null;

	return (
		<Collapsible open={open} onOpenChange={setOpen}>
			<CollapsibleTrigger asChild>
				<div className="group flex w-full items-start space-x-6">
					<span className="flex items-center space-x-6">
						{isSubFaq ? <span className="h-[2px] w-5 border-1 border-dashed  pr-0"></span> : null}
						<span className="my-1 grid size-5 shrink-0 place-content-center rounded-full border border-primary text-primary transition-colors duration-200 lg:group-hover:bg-primary lg:group-hover:text-white xl:size-6 xl:border-2">
							{open ? (
								<MinusIcon className="size-3 xl:size-3.5" />
							) : (
								<PlusIcon className="size-3 xl:size-3.5" />
							)}
						</span>
					</span>
					<div
						className={cn(
							open ? "text-primary" : "text-gray-900",
							"flex cursor-pointer items-center font-medium  md:text-lg lg:group-hover:text-primary  xl:text-xl"
						)}
					>
						<span className="font-lora text-lg hover:underline md:text-25">
							{parse(faq?.title)}
						</span>
					</div>
				</div>
			</CollapsibleTrigger>
			<CollapsibleContent className="ml-5">
				{/* FAQ QUICK SUMMARY */}
				{faq?.quick_summary ? (
					<div
						className={cn(
							"content py-1 pl-4 xl:pl-6",
							isSubFaq ? "my-4 ml-6 border-l-4 border-info text-primary" : "text-content"
						)}
					>
						{isSubFaq ? <b>Quick answer:</b> : null}
						{parse(faq.quick_summary)}
					</div>
				) : null}

				{/* FAQ PREVIEW */}
				<div className=" max-w-4xl bg-white">
					{faq?.summary ? (
						<React.Fragment>
							<div className="text-content pl-6">
								<FaqPreview page={page} faq={faq} />
							</div>
						</React.Fragment>
					) : null}
				</div>

				{/* FAQ CHILDREN / SUB FAQs */}
				<div className="mb-8 ml-5 space-y-8  border-l-2 border-dashed">
					{faq?.children?.map((subFaq: any, i: any) => (
						<div id={subFaq?.slug} key={i} className=" my-2 md:my-4">
							<Faqs faq={subFaq} isSubFaq={true} page={page} />
						</div>
					))}
				</div>
			</CollapsibleContent>
		</Collapsible>
	);
}

function FaqPreview({ className, faq, page }: any) {
	return (
		<>
			<div
				className={cn(
					"max-h-[12.25rem] overflow-hidden gradient-mask-b-60 xl:max-h-[15.75rem]",
					className
				)}
			>
				{parse(faq?.summary)}
			</div>
			<div>
				<Link
					href={`/faq/${faq?.slug}/`}
					className="tracking-2 group mt-4 flex items-start space-x-2 leading-7 text-examine-purple-400 hover:underline xl:text-xl xl:leading-9"
				>
					<span className="my-1 grid size-5 shrink-0 place-content-center rounded-full border border-primary transition-colors duration-300 lg:group-hover:bg-primary lg:group-hover:text-white xl:mt-2">
						<ArrowRight size="14" strokeWidth="1.7" />
					</span>{" "}
					<span>
						Click to open <span className="italic">&quot;{parse(faq?.title)}&quot;</span> in a new
						page
					</span>
				</Link>
			</div>
		</>
	);
}
